import type {
  ConnectionRouteInfo,
  ProcessorRuleConfig,
} from '../types/nodeConfig';
import type { ValidationIssue, ValidationReport } from '../types/validation';

const TRIGGER_EXPRESSION_HELP =
  'Supported: empty, all_requirements_met, requirement IDs, and, or, not, parentheses — e.g. (req_1 and req_2) or req_3';

function validateTriggerExpression(
  expression: string,
  requirementIds: string[],
): string | null {
  const trimmed = expression.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();
  if (lower === 'all_requirements_met') return null;

  const idPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g;
  const keywords = new Set(['and', 'or', 'not']);
  let match: RegExpExecArray | null;
  while ((match = idPattern.exec(trimmed)) !== null) {
    const token = match[1];
    if (keywords.has(token.toLowerCase())) continue;
    if (!requirementIds.includes(token)) {
      return `Unknown requirement id "${token}". ${TRIGGER_EXPRESSION_HELP}`;
    }
  }

  const opens = (trimmed.match(/\(/g) ?? []).length;
  const closes = (trimmed.match(/\)/g) ?? []).length;
  if (opens !== closes) {
    return `Unbalanced parentheses. ${TRIGGER_EXPRESSION_HELP}`;
  }

  return null;
}

function error(code: string, message: string, path?: string): ValidationIssue {
  return { code, severity: 'error', message, path };
}

function warn(code: string, message: string, path?: string): ValidationIssue {
  return { code, severity: 'warning', message, path };
}

export function validateProcessorDraft(
  node: Record<string, unknown>,
  incoming: ConnectionRouteInfo[],
  outgoing: ConnectionRouteInfo[],
): ValidationReport {
  const issues: ValidationIssue[] = [];
  const rules = (node.processor_rules as ProcessorRuleConfig[] | undefined) ?? [];
  const capacity = Number(node.capacity ?? 1);

  if (capacity <= 0) {
    issues.push(error('PROCESSOR_CAPACITY_INVALID', 'Capacity must be at least 1.'));
  }

  if (incoming.length === 0) {
    issues.push(warn('NO_INCOMING_ROUTES', 'Processor has no incoming routes connected.'));
  }
  if (outgoing.length === 0) {
    issues.push(warn('NO_OUTGOING_ROUTES', 'Processor has no outgoing routes connected.'));
  }

  if (rules.length === 0) {
    issues.push(warn('PROCESSOR_NO_RULES', 'Processor has no rules defined.'));
  }

  const enabledPriorities = new Map<number, string[]>();
  for (const rule of rules) {
    if (!rule.enabled) {
      issues.push(warn('PROCESSOR_RULE_DISABLED', `Rule "${rule.name}" is disabled.`));
      continue;
    }

    if (rule.input_requirements.length === 0) {
      issues.push(
        error(
          'PROCESSOR_RULE_INVALID',
          `Enabled rule "${rule.name}" requires at least one input requirement.`,
        ),
      );
    }
    if (rule.output_specs.length === 0) {
      issues.push(
        error(
          'PROCESSOR_RULE_INVALID',
          `Enabled rule "${rule.name}" requires at least one output spec.`,
        ),
      );
    }

    const bucket = enabledPriorities.get(rule.priority) ?? [];
    bucket.push(rule.name);
    enabledPriorities.set(rule.priority, bucket);

    for (const req of rule.input_requirements) {
      if (!incoming.some((r) => r.route_id === req.incoming_route_id)) {
        issues.push(
          error(
            'ROUTE_NOT_INCOMING',
            `Rule "${rule.name}": incoming route is not connected to this processor.`,
          ),
        );
      }
      if (req.min_count < 1) {
        issues.push(
          error(
            'INPUT_REQUIREMENT_MIN_COUNT',
            `Rule "${rule.name}": min count must be at least 1.`,
          ),
        );
      }
    }

    for (const spec of rule.output_specs) {
      if (!outgoing.some((r) => r.route_id === spec.target_route_id)) {
        issues.push(
          error(
            'ROUTE_NOT_OUTGOING',
            `Rule "${rule.name}": target route is not connected from this processor.`,
          ),
        );
      }
      if (spec.emit_count < 1) {
        issues.push(
          error('OUTPUT_SPEC_EMIT_COUNT', `Rule "${rule.name}": emit count must be at least 1.`),
        );
      }
      if ((spec.processing_delay_seconds ?? 0) < 0) {
        issues.push(
          error('PROCESSING_DELAY_NEGATIVE', `Rule "${rule.name}": delay cannot be negative.`),
        );
      }
      if ((spec.size_multiplier ?? 1) <= 0) {
        issues.push(
          error('OUTPUT_SPEC_SIZE_MULTIPLIER', `Rule "${rule.name}": size multiplier must be positive.`),
        );
      }

      const reqIds = rule.input_requirements.map((r) => r.id);
      const triggerErr = validateTriggerExpression(
        spec.trigger_expression ?? '',
        reqIds,
      );
      if (triggerErr) {
        issues.push(
          error('TRIGGER_EXPRESSION_INVALID', `Rule "${rule.name}": ${triggerErr}`),
        );
      }

      const bindingMode = spec.binding_mode ?? 'none';
      if (!['none', 'bound'].includes(bindingMode)) {
        issues.push(
          error(
            'BINDING_MODE_INVALID',
            `Rule "${rule.name}": binding_mode must be none or bound.`,
          ),
        );
      }
      if (bindingMode !== 'none' && !spec.bound_input_requirement_id) {
        issues.push(
          error(
            'BOUND_INPUT_REQUIREMENT_REQUIRED',
            `Rule "${rule.name}": bound output requires bound_input_requirement_id.`,
          ),
        );
      } else if (
        spec.bound_input_requirement_id &&
        !reqIds.includes(spec.bound_input_requirement_id)
      ) {
        issues.push(
          error(
            'MISSING_INPUT_REQUIREMENT_REFERENCE',
            `Rule "${rule.name}": bound_input_requirement_id not found in rule inputs.`,
          ),
        );
      }

      const scheduleMode = spec.schedule_mode ?? 'immediate';
      if (!['immediate', 'scheduled', 'delayed'].includes(scheduleMode)) {
        issues.push(
          error(
            'SCHEDULE_MODE_INVALID',
            `Rule "${rule.name}": schedule_mode must be immediate, scheduled, or delayed.`,
          ),
        );
      }
      if (
        scheduleMode !== 'immediate' &&
        (spec.schedule_entries ?? []).length === 0
      ) {
        issues.push(
          error(
            'SCHEDULE_ENTRIES_REQUIRED',
            `Rule "${rule.name}": schedule_entries required for ${scheduleMode} mode.`,
          ),
        );
      }
      for (const entry of spec.schedule_entries ?? []) {
        if (entry.delay_minutes < 0) {
          issues.push(
            error(
              'SCHEDULE_DELAY_NEGATIVE',
              `Rule "${rule.name}": schedule delay_minutes cannot be negative.`,
            ),
          );
        }
      }
    }
  }

  for (const [priority, names] of enabledPriorities) {
    if (names.length > 1) {
      issues.push(
        warn(
          'PROCESSOR_PRIORITY_OVERLAP',
          `Multiple enabled rules share priority ${priority}: ${names.join(', ')}.`,
        ),
      );
    }
  }

  return { issues };
}

export function explainProcessorRule(
  rule: ProcessorRuleConfig,
  incoming: ConnectionRouteInfo[],
  outgoing: ConnectionRouteInfo[],
  productNames: Map<string, string>,
): string {
  if (!rule.enabled) {
    return `Rule "${rule.name}" is disabled and will not run.`;
  }

  const logic = rule.logic_mode === 'or' ? 'any' : 'all';
  const inputs = rule.input_requirements
    .map((req) => {
      const route = incoming.find((r) => r.route_id === req.incoming_route_id);
      const product =
        req.product_type_id === '*'
          ? 'any product'
          : productNames.get(req.product_type_id) ?? req.product_type_id;
      return `≥${req.min_count} ${product} from ${route?.route_name ?? '?'}`;
    })
    .join(rule.logic_mode === 'or' ? ' OR ' : ' AND ');

  const outputs = rule.output_specs
    .map((spec) => {
      const route = outgoing.find((r) => r.route_id === spec.target_route_id);
      const delay =
        (spec.processing_delay_seconds ?? 0) > 0
          ? ` after ${spec.processing_delay_seconds}s`
          : '';
      const trigger =
        spec.trigger_expression?.trim()
          ? ` when ${spec.trigger_expression.trim()}`
          : '';
      const schedule =
        spec.schedule_mode && spec.schedule_mode !== 'immediate'
          ? ` (${spec.schedule_mode})`
          : '';
      const product = spec.output_product_type_id
        ? ` as ${productNames.get(spec.output_product_type_id) ?? spec.output_product_type_id}`
        : '';
      const size =
        spec.size_multiplier != null && spec.size_multiplier !== 1
          ? ` (×${spec.size_multiplier})`
          : '';
      return `emit ${spec.emit_count}${product}${size} → ${route?.route_name ?? '?'}${delay}${trigger}${schedule}`;
    })
    .join('; ');

  return `When ${logic} of (${inputs || 'no inputs'}), then ${outputs || 'no outputs'}.`;
}

export function mergeValidationReports(
  ...reports: (ValidationReport | null | undefined)[]
): ValidationReport {
  const seen = new Set<string>();
  const issues: ValidationIssue[] = [];
  for (const report of reports) {
    for (const issue of report?.issues ?? []) {
      const key = `${issue.severity}:${issue.code}:${issue.message}`;
      if (seen.has(key)) continue;
      seen.add(key);
      issues.push(issue);
    }
  }
  return { issues };
}
